import { ref, computed, watch } from "vue";

/**
 * Base composable for table data management
 * Provides common functionality for sorting, pagination, and selection
 * @param {Object} options - Configuration options
 * @param {String} options.sortBy - Initial sort field
 * @param {Boolean} options.sortDesc - Initial sort direction
 * @param {Object} options.filters - Initial filters
 * @returns {Object} Table state and methods
 */
export function useEgTable(options = {}) {
  const { sortBy = "", sortDesc = false, filters = {} } = options;

  // State
  const selectedIds = ref([]);
  const selectedAll = ref(false);
  const currentSortBy = ref(sortBy);
  const currentSortDesc = ref(sortDesc);
  const currentFilters = ref({ ...filters });
  const tableLoading = ref(false);
  const initialLoading = ref(true);

  // Computed
  const isFiltered = computed(() => {
    return Object.keys(currentFilters.value).some(
      (key) =>
        currentFilters.value[key] !== null &&
        currentFilters.value[key] !== undefined &&
        currentFilters.value[key] !== ""
    );
  });

  const hasSelectedItems = computed(() => selectedIds.value.length > 0);

  // Watchers
  watch(
    () => selectedIds.value.length,
    () => {
      if (selectedIds.value.length === 0) {
        selectedAll.value = false;
      }
    }
  );

  // Methods
  function resetSelection() {
    selectedIds.value = [];
    selectedAll.value = false;
  }

  function onSort(field) {
    if (!field.egSortable) return;

    const sortDesc =
      field.key === currentSortBy.value ? !currentSortDesc.value : false;

    currentSortBy.value = field.key;
    currentSortDesc.value = sortDesc;

    return {
      sortBy: field.key,
      sortDesc: sortDesc,
    };
  }

  function updateFilters(newFilters) {
    currentFilters.value = { ...currentFilters.value, ...newFilters };
  }

  function clearFilters() {
    currentFilters.value = {};
  }

  function setLoading(loading) {
    tableLoading.value = loading;
  }

  function setInitialLoading(loading) {
    initialLoading.value = loading;
  }

  function toggleSelectAll() {
    selectedAll.value = !selectedAll.value;
    if (!selectedAll.value) {
      selectedIds.value = [];
    }
  }

  return {
    // State
    selectedIds,
    selectedAll,
    currentSortBy,
    currentSortDesc,
    currentFilters,
    tableLoading,
    initialLoading,

    // Computed
    isFiltered,
    hasSelectedItems,

    // Methods
    resetSelection,
    onSort,
    updateFilters,
    clearFilters,
    setLoading,
    setInitialLoading,
    toggleSelectAll,
  };
}
